import type { Dispatch, PropsWithChildren, SetStateAction } from "react";
import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useGlobalSearchParams } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  ArrowRight,
  Button,
  Checkbox,
  CheckBoxWithLabel,
  Chips,
  InputWithLabel,
  MatchCase,
  Scrollbar,
  StepProgress,
  Table,
  WarningSign,
  XInCircle,
} from "@ptsd/uikit";
import type { PaginationState } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ViewProps } from "tamagui";
import {
  ScrollView,
  Separator,
  Text,
  Button as TGButton,
  Form as TGForm,
  View,
  XStack,
  YStack,
} from "tamagui";
import { z } from "zod";

import { api, vanillaApi } from "~/utils/api";
import { Pagination, RowVirtualizer } from "~/components/blocks";
import { RowFloatingPanel } from "~/components/blocks/row-floating-panel";
import { useCalculatePopoverPosition } from "~/hooks";
import type { Slices } from "./types";

enum Step {
  Name,
  Groups,
  Users,
  Confirm,
}

// TODO: should be refactored and a better abstraction found for multi-step state management.

const TOTAL_STEPS_IN_CREATE_RESEARCH_FUNNEL = 4;

//TODO: move to i18n after zod i18n integration
const ResearchFunnelSchema = z.object({
  name: z
    .string()
    .min(1, "Назва дослідження не може бути порожньою")
    .max(100, "Назва дослідження не може бути довшою за 100 символів")
    .superRefine(async (name, ctx) => {
      const isTaken =
        await vanillaApi.research.checkResearchNameUniqueness.query({ name });

      if (isTaken) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Назва дослідження має бути унікальною",
        });
      }
    }),
  groupIds: z.array(z.string()).optional(),
  allowedUsersFromGroups: z.array(z.string()).optional(),
  users: z.array(
    z.object({
      id: z.string(),
      email: z.string().email(),
      groupName: z.string().optional(),
      groupId: z.string(),
    }),
  ),
});

const ResearchFunnelSchemaKeys = Object.keys(ResearchFunnelSchema.shape);

export type ResearchFunnelSchemaType = z.infer<typeof ResearchFunnelSchema>;
type ResearchFunnelSchemaKeysType = keyof typeof ResearchFunnelSchema.shape;

interface CreateResearchFunnelContext {
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  totalSteps: number;
}

const CreateResearchFunnelContext = createContext<CreateResearchFunnelContext>({
  step: 0,
  setStep: () =>
    console.warn("must be used inside a CreateResearchFunnelProvider"),
  totalSteps: TOTAL_STEPS_IN_CREATE_RESEARCH_FUNNEL,
});

const CreateResearchFunnelProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [step, setStep] = useState(0);

  return (
    <CreateResearchFunnelContext.Provider
      value={{
        step,
        setStep,
        totalSteps: TOTAL_STEPS_IN_CREATE_RESEARCH_FUNNEL,
      }}
    >
      {children}
    </CreateResearchFunnelContext.Provider>
  );
};

const useCreateResearchFunnel = () => {
  const context = useContext(CreateResearchFunnelContext);
  if (!context) {
    throw new Error(
      "useCreateResearchFunnel must be used within a CreateResearchFunnelProvider",
    );
  }

  const isLastStep = context.step === context.totalSteps - 1;
  const isFirstStep = context.step === 0;

  const next = () => context.setStep((prev) => (!isLastStep ? prev + 1 : prev));
  const back = () =>
    context.setStep((prev) => (!isFirstStep ? prev - 1 : prev));

  return { ...context, next, back, isLastStep, isFirstStep };
};

const Progress: React.FC<ViewProps> = (props) => {
  const { step, totalSteps } = useCreateResearchFunnel();
  return (
    <StepProgress
      maxWidth={916}
      steps={totalSteps}
      current={step + 1}
      {...props}
    />
  );
};

const Controls = () => {
  const { next, back, isFirstStep, step } = useCreateResearchFunnel();
  const form = useFormContext<ResearchFunnelSchemaType>();
  const { t } = useTranslation("research");

  return (
    <XStack
      data-id="create-research-funnel-controls"
      gap={"$0.5"}
      paddingTop={"$3.3"}
    >
      {!isFirstStep && (
        <Button
          mode="icon"
          onPress={back}
          variant="gray"
          rounded="lg"
          size="md"
        >
          <ArrowLeft />
        </Button>
      )}

      {step === (Step.Confirm as number) ? (
        <TGForm.Trigger asChild>
          <Button
            data-id="controls-button-confirm"
            maxWidth={220}
            width="100%"
            variant="primary"
            size="md"
            rounded="lg"
            iconAfter={<ArrowRight color="white" />}
          >
            {t("create-research-funnel.controls.confirm")}
          </Button>
        </TGForm.Trigger>
      ) : (
        <Button
          data-id="controls-button-next"
          maxWidth={220}
          width="100%"
          variant="primary"
          size="md"
          rounded="lg"
          iconAfter={<ArrowRight color="white" />}
          onPress={() => {
            void form
              .trigger(
                ResearchFunnelSchemaKeys[step] as ResearchFunnelSchemaKeysType,
              )
              .then((isValid) => {
                if (isValid) next();
              })
              .catch(console.error);
          }}
        >
          {t("create-research-funnel.controls.confirm")}
        </Button>
      )}
    </XStack>
  );
};

const Form: React.FC<
  PropsWithChildren & { onSubmit: (data: ResearchFunnelSchemaType) => void }
> = ({ children, onSubmit }) => {
  const form = useForm({
    resolver: zodResolver(ResearchFunnelSchema, { async: true }),
    defaultValues: {
      name: "",
      groupIds: [],
      users: [],
      allowedUsersFromGroups: [],
    },
  });

  return (
    <TGForm
      data-id="create-research-funnel-form"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <FormProvider {...form}>{children}</FormProvider>
    </TGForm>
  );
};

const CreateResearchFunnelProgress = () => (
  <Progress data-id="create-research-funnel-progress" />
);

const CreateResearchFunnelHeader = () => {
  const { step } = useCreateResearchFunnel();
  const { t } = useTranslation("research");
  const { title, description } =
    t("create-research-funnel.header-texts", { returnObjects: true })[step] ??
    {};

  return (
    <View gap={"$1"} maxWidth={888}>
      <Text
        data-id="create-research-funnel-header-title"
        fontFamily={"$body"}
        fontWeight={"$6"}
        fontSize={"$6"}
        lineHeight={"$6"}
        color="$gray-950"
      >
        {title}
      </Text>
      <Text
        data-id="create-research-funnel-header-description"
        fontFamily={"$body"}
        fontWeight={"$4"}
        fontSize={"$3"}
        lineHeight={"$3"}
        color="$gray-800"
      >
        {description}
      </Text>
    </View>
  );
};

const ResearchNameField = () => {
  const form = useFormContext<ResearchFunnelSchemaType>();
  const { t } = useTranslation("research");

  return (
    <View gap={"$7.4"}>
      <View gap={"$1.3"} maxWidth={450}>
        <WarningSign color={"#F9709A"} />
        <Text
          fontFamily={"$body"}
          fontWeight={"$6"}
          fontSize={"$2"}
          lineHeight={"$2"}
          color={"$gray-primary-900"}
        >
          {t("create-research-funnel.first-step-research-name.warning-message")}
        </Text>
      </View>
      <Controller
        name="name"
        {...form}
        render={({ field, fieldState }) => {
          const { ref: _, onChange, ...rest } = field;
          return (
            <InputWithLabel
              maxWidth={440}
              variant={"inline"}
              placeholder={t(
                "create-research-funnel.first-step-research-name.input-placeholder",
              )}
              label={t(
                "create-research-funnel.first-step-research-name.input-label",
              )}
              onChangeText={onChange}
              isError={fieldState.invalid}
              error={fieldState.error?.message}
              {...rest}
            />
          );
        }}
      />
    </View>
  );
};

const SelectGroups = () => {
  const { type } = useGlobalSearchParams<{
    type: Slices;
  }>();
  const form = useFormContext<ResearchFunnelSchemaType>();

  const allowedUsersFromGroups = useWatch({
    name: "allowedUsersFromGroups",
    control: form.control,
  });

  const selectedUsers = useWatch({
    name: "users",
    control: form.control,
  });

  const [data] = api.group.getGroupNamesWithId.useSuspenseQuery();

  const [users] = api.research.getAvailableUsers.useSuspenseQuery(
    {
      type,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    },
  );

  return (
    <RowVirtualizer
      data-id="select-groups"
      count={data.length}
      estimateSize={44}
      height={415}
      maxWidth={440}
      contentContainerStyle={{
        paddingLeft: "$2",
      }}
    >
      {(virtualItem) => {
        const group = data[virtualItem.index];
        if (group === undefined) return null;
        return (
          <View
            key={virtualItem.key}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            <Controller
              name="groupIds"
              {...form}
              render={({ field }) => {
                const { ref: _, onChange, value } = field;
                return (
                  <View height="$5.3" justifyContent="flex-end">
                    <View
                      height="$3.4"
                      borderBottomWidth={"$0.2"}
                      borderBottomColor={"$gray-100"}
                    >
                      <CheckBoxWithLabel
                        data-id={"group-checkbox"}
                        checked={value?.includes(group.id)}
                        renderLabel={
                          <Text
                            data-id={"group-checkbox-label"}
                            fontFamily="$body"
                            color="$gray-950"
                            fontWeight="$5"
                            fontSize="$2"
                            lineHeight="$2"
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {group?.name}
                          </Text>
                        }
                        onCheckedChange={(checked) => {
                          if (value === undefined) return;

                          if (checked) {
                            onChange([...value, group.id]);
                          } else {
                            onChange(
                              value?.filter((value) => value !== group.id),
                            );
                          }

                          const incomingUsers = checked
                            ? users.filter(
                                ({ groupId, activeResearchName }) =>
                                  group.id === groupId &&
                                  activeResearchName === undefined,
                              )
                            : [];

                          if (!checked) {
                            form.setValue(
                              "allowedUsersFromGroups",
                              allowedUsersFromGroups?.filter(
                                (id) => id !== group.id,
                              ),
                            );
                          }

                          form.setValue(
                            "users",
                            Array.from(
                              new Map(
                                [...selectedUsers, ...incomingUsers].map(
                                  (item) => [item.id, item],
                                ),
                              ).values(),
                            ).filter(({ groupId }) =>
                              [
                                ...(checked
                                  ? allowedUsersFromGroups ?? []
                                  : allowedUsersFromGroups?.filter(
                                      (id) => id !== group.id,
                                    ) ?? []),
                                ...(checked ? [group.id] : []),
                              ]?.includes(groupId),
                            ),
                          );
                        }}
                      />
                    </View>
                  </View>
                );
              }}
            />
          </View>
        );
      }}
    </RowVirtualizer>
  );
};

const SelectUsers = () => {
  const [email, setEmail] = useState<string>("");
  const { type } = useGlobalSearchParams<{
    type: Slices;
  }>();

  const form = useFormContext<ResearchFunnelSchemaType>();

  const users = useWatch({
    name: "users",
    control: form.control,
  });

  useEffect(() => {
    const groupIds = Array.from(new Set(users.map(({ groupId }) => groupId)));
    form.setValue("allowedUsersFromGroups", groupIds, { shouldDirty: true });
  }, [form, users]);

  const { t } = useTranslation("research");

  const [data] = api.research.getAvailableUsers.useSuspenseQuery(
    {
      type,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    },
  );

  const filteredUsers = useMemo(
    () =>
      data?.filter(
        ({ email: participantEmail, activeResearchName }) =>
          participantEmail.toLowerCase().includes(email.toLowerCase().trim()) &&
          activeResearchName === undefined,
      ),

    [data, email],
  );

  const activeResearchName = useMemo(
    () =>
      data?.find((participant) => participant.email === email)
        ?.activeResearchName,
    [data, email],
  );

  const selectAll = useCallback(() => {
    form.setValue("users", filteredUsers);
  }, [filteredUsers, form]);

  return (
    <View data-id="select-users">
      <YStack gap={"$3"}>
        <Button
          onPress={selectAll}
          rounded="lg"
          variant={"outlined"}
          size={"md"}
          maxWidth={340}
        >
          {t("create-research-funnel.third-step-research-users.add-all-btn")}
        </Button>
        <Text fontFamily="$body" color="$gray-600" marginBottom={"$1"}>
          {t("create-research-funnel.third-step-research-users.add-or-select")}
        </Text>
        <InputWithLabel
          isError={activeResearchName !== undefined}
          error={t(
            "create-research-funnel.third-step-research-users.input-error-message",
            { name: activeResearchName },
          )}
          label={t(
            "create-research-funnel.third-step-research-users.input-label",
          )}
          variant="inline"
          placeholder={t(
            "create-research-funnel.third-step-research-users.input-placeholder",
          )}
          maxWidth={440}
          onChangeText={setEmail}
        />
      </YStack>
      <RowVirtualizer
        count={filteredUsers?.length ?? 0}
        estimateSize={48}
        height={265}
        maxWidth={420}
        contentContainerStyle={{
          paddingLeft: "$2",
        }}
      >
        {(virtualItem) => {
          if (!filteredUsers) return null;
          const user = filteredUsers[virtualItem.index];
          if (user === undefined) return null;
          return (
            <View
              key={virtualItem.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <Controller
                name="users"
                control={form.control}
                render={({ field }) => {
                  const { ref: _, onChange, value } = field;
                  return (
                    <View height="$5.3" justifyContent="flex-end">
                      <View
                        height="$3.4"
                        borderBottomWidth={"$0.2"}
                        borderBottomColor={"$gray-100"}
                      >
                        <CheckBoxWithLabel
                          data-id={"user-checkbox"}
                          checked={value?.map((u) => u.id).includes(user?.id)}
                          renderLabel={
                            <Text
                              data-id={"user-checkbox-label"}
                              fontFamily="$body"
                              color="$gray-950"
                              fontWeight="$5"
                              fontSize="$2"
                              lineHeight="$2"
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {user?.email}
                            </Text>
                          }
                          onCheckedChange={(checked) => {
                            if (value === undefined) return;
                            return checked
                              ? onChange([...value, user])
                              : onChange(
                                  value?.filter(
                                    (value) => value.id !== user?.id,
                                  ),
                                );
                          }}
                        />
                      </View>
                    </View>
                  );
                }}
              />
            </View>
          );
        }}
      </RowVirtualizer>
    </View>
  );
};

const SelectedUsers = () => {
  const { t } = useTranslation("research");
  const form = useFormContext<ResearchFunnelSchemaType>();
  const users = form.watch("users");
  if (!users || users.length === 0) return null;

  return (
    <YStack gap={"$1"} marginTop={"$2"}>
      <Text
        fontFamily="$body"
        fontWeight={"$6"}
        fontSize={"$6"}
        lineHeight={"$6"}
      >
        {t("create-research-funnel.third-step-research-users.selected-users")}
      </Text>
      <Scrollbar.Wrapper gap={"$2"}>
        <Scrollbar.Track
          alignItems="center"
          backgroundColor={"$gray-200"}
          thickness={2}
        >
          <Scrollbar.Thumb
            backgroundColor={"$green-500"}
            borderRadius={"$5"}
            thickness={4}
          />
        </Scrollbar.Track>
        <ScrollView
          data-id="selected-users"
          paddingVertical={"$3"}
          maxWidth={1008}
          borderBottomColor={"$gray-100"}
          borderBottomWidth={"$0.2"}
          height={130}
        >
          <View flexWrap="wrap" flexDirection="row" gap={"$1.3"}>
            {users?.map((user) => (
              <Chips.Chip
                key={user.id}
                data-id="selected-user-chip"
                selected={false}
                backgroundColor="#0B564C"
                gap={"$0.5"}
              >
                {(ChipText, conf) => (
                  <>
                    <ChipText color={conf.backgroundColor}>
                      {user.email}
                    </ChipText>
                    <TGButton
                      cursor="pointer"
                      onPress={() =>
                        form.setValue(
                          "users",
                          users?.filter((u) => u.id !== user.id),
                        )
                      }
                      padding={0}
                      unstyled
                    >
                      <XInCircle />
                    </TGButton>
                  </>
                )}
              </Chips.Chip>
            ))}
          </View>
        </ScrollView>
      </Scrollbar.Wrapper>
    </YStack>
  );
};

type User = NonNullable<ResearchFunnelSchemaType["users"]>[number];

const columnHelper = createColumnHelper<User>();

const createColumns = ({ onDelete }: { onDelete: (id: string) => void }) => {
  return [
    columnHelper.accessor((row) => ({ id: row.id }), {
      header: ({ table }) => {
        return (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      id: "select",
    }),

    columnHelper.accessor("email", {
      header: "Учасник",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("groupName", {
      header: "Група",
      cell: (info) => info.getValue(),
    }),

    columnHelper.accessor((row) => ({ id: row.id }), {
      header: "Керування",
      cell: ({ row }) => {
        return (
          <Button
            width={"100%"}
            height={"$4"}
            variant={"danger"}
            size={"sm"}
            maxWidth={235}
            rounded="lg"
            onPress={() => onDelete(row.original.id)}
            //NOTE: used hex coz it is not tamagui icon
            icon={<XInCircle color={"#D6453B"} />}
          >
            Видалити учасника
          </Button>
        );
      },
      enableSorting: false,
      enableHiding: false,
      id: "delete",
    }),
  ];
};

const LIMIT = 10;

const SelectedUsersTable = () => {
  const form = useFormContext<ResearchFunnelSchemaType>();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: LIMIT,
  });

  const table = useReactTable({
    data: form.watch("users") ?? [],
    columns: createColumns({
      onDelete: (id) => {
        const filtered = form.getValues("users")?.filter((u) => u.id !== id);
        form.setValue("users", filtered);
      },
    }),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  const pageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount();

  const selectedRowModel = table.getSelectedRowModel();
  const selectedRows = selectedRowModel.rows.map((row) => row.original);
  const selectedUserIds = selectedRows.map((user) => user.id);

  const firstSelectedRow = selectedRows[selectedRows.length - 1];

  const { rowRef, popoverPosition } = useCalculatePopoverPosition(
    firstSelectedRow ?? {},
  );

  return (
    <View gap={"$4"} maxWidth={888} position="relative">
      {selectedRows.length > 0 ? (
        <RowFloatingPanel.Body
          data-id="row-floating-panel"
          right={150}
          position={{ y: popoverPosition.y + 110 }}
        >
          <RowFloatingPanel.Button
            data-id="deactivate-button"
            color={"$darkpink-500"}
            onPress={() => {
              const filtered = form
                .getValues("users")
                ?.filter((u) => !selectedUserIds.includes(u.id));
              form.setValue("users", filtered);
              table.resetRowSelection();
            }}
          >
            Видалити
          </RowFloatingPanel.Button>
        </RowFloatingPanel.Body>
      ) : null}

      <Table
        alignCells={{ x: "center", y: "center" }}
        alignHeaderCells={{ y: "center", x: "center" }}
        cellHeight="$5.8"
        data-id="users-table"
      >
        <Table.Head data-id="users-table-head">
          {headerGroups.map((headerGroup, index) => {
            return (
              <Table.Row
                data-id="users-table-header-row"
                rowLocation={
                  index === 0
                    ? "first"
                    : index === headerGroups.length - 1
                      ? "last"
                      : "middle"
                }
                key={headerGroup.id}
                justifyContent="flex-start"
              >
                {headerGroup.headers.map((header, index) => {
                  return (
                    <Table.HeaderCell
                      data-id={"users-table-header-cell-" + header.id}
                      backgroundColor="white"
                      key={header.id}
                      justifyContent="flex-start"
                      borderWidth={0}
                      cellLocation={
                        index === 0
                          ? "first"
                          : index === headerGroup.headers.length - 1
                            ? "last"
                            : "middle"
                      }
                      maxWidth={header.id === "select" ? 40 : undefined}
                    >
                      <Text
                        fontFamily={"$subtitle"}
                        fontSize={"$2"}
                        lineHeight={"$2"}
                        fontWeight={"$5"}
                        color={"$gray-primary-900"}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Text>
                    </Table.HeaderCell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Head>

        <Table.Body data-id="users-table-body">
          {tableRows.length === 0 ? (
            <Table.Row
              data-id="users-table-row"
              borderBottomRightRadius={"$1"}
              borderBottomLeftRadius={"$1"}
              backgroundColor={"white"}
              key={1}
            >
              <Table.Cell
                data-id="users-table-no-data-cell"
                borderColor={"$gray-200"}
                width={"100%"}
                key={1}
                justifyContent="center"
              >
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  fontFamily={"$subtitle"}
                  fontSize={"$2"}
                  lineHeight={"$2"}
                  color={"$gray-primary-900"}
                >
                  Немає даних
                </Text>
              </Table.Cell>
            </Table.Row>
          ) : (
            tableRows?.map((row, index) => {
              return (
                <Table.Row
                  data-id="users-table-row"
                  backgroundColor={index % 2 === 0 ? "white" : "$gray-50"}
                  selected={row.getIsSelected()}
                  ref={(node) => {
                    if (node) rowRef.current[row.original.id] = node;
                  }}
                  rowLocation={
                    index === 0
                      ? "first"
                      : index === tableRows.length - 1
                        ? "last"
                        : "middle"
                  }
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <Table.Cell
                        data-id={"users-table-cell-" + cell.id}
                        justifyContent="flex-start"
                        cellLocation={
                          index === 0
                            ? "first"
                            : row.getVisibleCells().length - 1 === index
                              ? "last"
                              : "middle"
                        }
                        key={cell.id}
                        flex={1}
                        maxWidth={cell.column.id === "select" ? 40 : undefined}
                      >
                        <Text
                          width={"100%"}
                          fontFamily={"$subtitle"}
                          lineHeight={"$2"}
                          fontSize={"$2"}
                          fontWeight={"$4"}
                          color={"$gray-primary-900"}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Text>
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })
          )}
        </Table.Body>
      </Table>
      <Pagination
        currentPage={pageIndex + 1}
        onPageChange={(page) => {
          table.setPageIndex(page - 1);
        }}
        totalPages={totalPages}
        withoutNavigationButtons
        width={"auto"}
        justifyContent={"flex-start"}
        alignSelf="flex-start"
        margin={0}
      />
    </View>
  );
};

const CurrentFormField = () => {
  const { step } = useCreateResearchFunnel();
  return (
    <MatchCase value={step}>
      {(Case) => (
        <>
          <Case condition={Step.Name}>
            <ResearchNameField />
          </Case>
          <Case condition={Step.Groups}>
            <Suspense>
              <SelectGroups />
            </Suspense>
          </Case>
          <Case condition={Step.Users}>
            <View gap={"$1.3"}>
              <Suspense>
                <SelectUsers />
              </Suspense>
              <SelectedUsers />
            </View>
          </Case>
          <Case condition={Step.Confirm}>
            <SelectedUsersTable />
            <Separator paddingTop={"$3"} borderColor={"$gray-100"} />
          </Case>
        </>
      )}
    </MatchCase>
  );
};

export const CreateResearchFunnel = {
  Provider: CreateResearchFunnelProvider,
  Progress: CreateResearchFunnelProgress,
  Form,
  Controls,
  CurrentFormField,
  Header: CreateResearchFunnelHeader,
};
